import type { City, Program, ProgramStatus, ServiceArea, SportSlug, Venue } from '../types/landing';

export interface VenueInventory extends Venue {
  areaId: string;
  areaName: string;
  sportPrograms: Program[];
}

export interface AreaInventory {
  area: ServiceArea;
  venues: VenueInventory[];
}

const statusRank: Record<ProgramStatus, number> = { open: 0, waitlist: 1, upcoming: 2 };

export function buildInventory(city: City, sport: SportSlug): AreaInventory[] {
  return city.areas
    .map((area) => ({
      area,
      venues: area.venues
        .map((venue) => ({
          ...venue,
          areaId: area.id,
          areaName: area.name,
          sportPrograms: venue.programs.filter((p) => p.sport === sport),
        }))
        .filter((v) => v.sportPrograms.length > 0),
    }))
    .filter((a) => a.venues.length > 0);
}

export function allVenuesIn(city: City): VenueInventory[] {
  return city.areas.flatMap((area) =>
    area.venues.map((v) => ({ ...v, areaId: area.id, areaName: area.name, sportPrograms: v.programs })),
  );
}

export function sportsOfferedIn(city: City): SportSlug[] {
  const set = new Set<SportSlug>();
  city.areas.forEach((a) => a.venues.forEach((v) => v.programs.forEach((p) => set.add(p.sport))));
  return Array.from(set);
}

export function bestStatus(programs: Program[]): ProgramStatus {
  return programs.reduce<ProgramStatus>(
    (best, p) => (statusRank[p.status] < statusRank[best] ? p.status : best),
    'upcoming',
  );
}

export function summarizePrograms(programs: Program[]) {
  const ages = Array.from(new Set(programs.map((p) => p.ages)));
  const days = Array.from(new Set(programs.map((p) => p.day)));
  const first = [...programs].sort((a, b) => statusRank[a.status] - statusRank[b.status])[0];
  return {
    ages: `Ages ${ages.join(', ')}`,
    days: days.join(' & '),
    season: first ? `${first.season} · starts ${first.starts}` : '',
  };
}

export function venueSportUrl(citySlug: string, venueId: string, sportSlug: string): string {
  return `/${citySlug}/${venueId}/${sportSlug}`;
}
